<script setup lang="ts">
import { PV } from "@/widgets";
import { useGeolocation } from "@vueuse/core";
import { isFunction, isNil, isNotNil } from "es-toolkit";
import type { Map, Marker } from "leaflet";
import markerRetina from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { computed, nextTick, onMounted, reactive, ref, useId, watch, watchEffect } from "vue";
import LatIcon from "~icons/mdi/latitude";
import LngIcon from "~icons/mdi/longitude";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormExt, FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = string;
interface PropsType extends BaseProps<ModelType> {
    formId?: string;
    placeholder?: string;
    initialCoord?: (() => Promise<[number, number] | null | undefined>) | [number, number];
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
});

const slots = defineSlots<{
    icon?: () => unknown;
}>();

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

const mapId = useId();
const mapVisible = ref(false);
const coordLoadingVisible = ref(false);
const geo = useGeolocation({ enableHighAccuracy: true, immediate: false });
const location: { lat: number; lng: number } = reactive({
    lat: -7.329570911668107,
    lng: 108.23963820934297,
});

const map = ref<Map>();
const marker = ref<Marker>();
const latlng = computed(() => [location.lat, location.lng] as [number, number]);

watchEffect(() => {
    if (model.value === "" || model.value === null) {
        model.value = undefined;
    }
});

async function showMap() {
    geo.resume();
    mapVisible.value = true;
    await setupLeaflet();
}

async function setupLeaflet() {
    const L = await import("leaflet");

    if (model.value !== undefined) {
        const parts = model.value.split(", ");
        location.lat = parseFloat(parts[0] ?? "0");
        location.lng = parseFloat(parts[1] ?? "0");
    } else {
        onPinpoint();
    }

    map.value = L.map(mapId, {
        attributionControl: false,
        minZoom: 6,
        maxZoom: 22,
        maxBounds: [
            [-90, -180],
            [90, 180],
        ],
        maxBoundsViscosity: 1.0,
    });

    marker.value = L.marker(latlng.value, {
        icon: L.icon({
            iconUrl: markerIcon,
            shadowUrl: markerShadow,
            iconRetinaUrl: markerRetina,
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            tooltipAnchor: [16, -28],
            shadowSize: [41, 41],
        }),
    });

    const tileLayer = L.tileLayer("http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}", {
        minZoom: 6,
        maxZoom: 22,
        tileSize: 256,
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        noWrap: true,
    });

    marker.value.addTo(map.value);
    tileLayer.addTo(map.value);
    map.value.setView(latlng.value, 20);
    marker.value.setLatLng(latlng.value);

    if (state.editMode) {
        map.value.on("click", (data) => {
            marker.value?.setLatLng(data.latlng);
            location.lat = data.latlng.lat;
            location.lng = data.latlng.lng;
        });
    }
}

async function onPinpoint() {
    const coords = geo.coords.value;
    if (coords.latitude === Infinity || coords.longitude === Infinity) {
        return;
    }

    location.lat = coords.latitude;
    location.lng = coords.longitude;
    await nextTick();
}

function onSubmit() {
    model.value = `${location.lat}, ${location.lng}`;
    mapVisible.value = false;
    geo.pause();
}

onMounted(async () => {
    if (model.value !== undefined) return;
    if (isFunction(props.initialCoord)) {
        coordLoadingVisible.value = true;
        const coord = await props.initialCoord();
        coordLoadingVisible.value = false;
        if (isNotNil(coord)) {
            const [lat, lng] = coord;
            location.lat = lat;
            location.lng = lng;
        }
    } else if (Array.isArray(props.initialCoord)) {
        const [lat, lng] = props.initialCoord;
        location.lat = lat;
        location.lng = lng;
    }
});

watch(
    () => [location.lat, location.lng],
    () => {
        if (isNil(map.value) || isNil(marker.value)) return;
        marker.value.setLatLng(latlng.value);
        if (!map.value.getBounds().contains(marker.value.getLatLng())) {
            map.value.setView(latlng.value);
        }
    },
);
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.InputText v-model="model" class="rns-input-static" disabled fluid />
                <FormExt v-if="state.updatable || state.removable" :actions="actions" :state="state" />
                <Buttons.ExtMarker v-else-if="state.hasValue" @click="showMap" />
            </PV.IconField>
        </template>
        <template v-else>
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.InputText
                    v-model.trim="model"
                    class="hover:cursor-pointer"
                    :disabled="props.disabled"
                    fluid
                    :form="props.formId"
                    :inputId="state.id"
                    :invalid="state.hasError"
                    :placeholder="props.placeholder ?? `Masukkan ${label}`"
                    :readonly="true"
                    :required="props.required"
                    @change="actions.onValidate"
                    @click="showMap"
                    @keyup.enter.prevent="actions.onSubmit"
                />
                <Buttons.ExtReset v-if="state.clearable && state.hasValue" @click="actions.onReset" />
            </PV.IconField>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />

        <PV.Drawer v-model:visible="mapVisible" position="full">
            <template #header>
                <div class="w-full pr-4 md:pr-0 flex flex-col md:flex-row items-center justify-start gap-2">
                    <PV.IconField class="w-full md:w-auto md:min-w-12">
                        <PV.InputNumber
                            v-model="location.lat"
                            :allowEmpty="false"
                            fluid
                            :max="90"
                            :maxFractionDigits="15"
                            :min="-90"
                            :readonly="!state.editMode"
                        />
                        <PV.InputIcon class="-mt-2.5!">
                            <LatIcon />
                        </PV.InputIcon>
                    </PV.IconField>

                    <PV.IconField class="w-full md:w-auto md:min-w-12">
                        <PV.InputNumber
                            v-model="location.lng"
                            :allowEmpty="false"
                            fluid
                            :max="180"
                            :maxFractionDigits="15"
                            :min="-180"
                            :readonly="!state.editMode"
                        />
                        <PV.InputIcon class="-mt-2.5!">
                            <LngIcon />
                        </PV.InputIcon>
                    </PV.IconField>
                </div>
            </template>

            <template v-if="state.editMode" #footer>
                <div class="flex flex-col md:flex-row gap-2 md:gap-0 items-center justify-between w-full">
                    <PV.Button
                        v-if="isNotNil(geo.locatedAt.value)"
                        class="w-full md:w-auto"
                        label="Gunakan Lokasi Saat Ini"
                        severity="info"
                        @click="onPinpoint"
                    />
                    <PV.Button
                        v-else
                        class="w-full md:w-auto"
                        disabled
                        label="Mencari Lokasi Saat Ini"
                        severity="secondary"
                    />

                    <PV.Message
                        v-if="isNotNil(geo.error.value?.code)"
                        class="w-full md:w-auto inline-flex justify-center"
                        severity="error"
                    >
                        <template v-if="geo.error.value.code === 1">
                            Tidak mendapat izin untuk mengakses lokasi.
                        </template>
                        <template v-if="geo.error.value.code === 2">
                            Terjadi kesalahan, tidak dapat menentukan lokasi saat ini.
                        </template>
                        <template v-if="geo.error.value.code === 3">
                            Gagal mendapatkan lokasi dalam waktu yang sudah ditentukan.
                        </template>
                    </PV.Message>

                    <PV.Button class="w-full md:w-auto" label="Simpan" @click="onSubmit" />
                </div>
            </template>

            <div class="relative h-full w-full">
                <div :id="mapId" ref="mapElement" class="rounded-sm shadow-sm w-full h-full z-10!"></div>
                <div
                    v-if="coordLoadingVisible"
                    class="absolute bg-black/60 left-0 right-0 top-0 bottom-0 text-white z-20 flex items-center justify-center font-bold text-3xl"
                >
                    MEMUAT TITIK KOORDINAT AWAL ...
                </div>
            </div>
        </PV.Drawer>
    </div>
</template>

<style>
@import "@npm/leaflet/dist/leaflet.css";
</style>

<style scoped>
@reference "@/styles/main.css";

.rns-input-static {
    @apply text-gray-700! bg-transparent! shadow-none! px-1! pt-2.25!;
    @apply rns-g-static-input;
}
</style>
